import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Tag, Form, Input, Switch, InputNumber, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

interface AdminMenuDto {
    id: string;
    name: string;
    url: string;
    orderIndex: number;
    openInNewWindow: boolean;
    availableLanguages: string[];
    subMenus: AdminMenuDto[];
    children?: AdminMenuDto[]; // Used by AntD Table
}

export const Menus: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingMenuId, setEditingMenuId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const { data: menus, isLoading } = useQuery({
        queryKey: ['adminMenus'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/menus');
            // Map subMenus to children for AntD TreeTable
            const mapChildren = (items: AdminMenuDto[]): AdminMenuDto[] => {
                return items.map(item => ({
                    ...item,
                    children: item.subMenus && item.subMenus.length > 0 ? mapChildren(item.subMenus) : undefined
                }));
            };
            return mapChildren(response.data.data || []);
        }
    });

    const { refetch: fetchMenuDetails } = useQuery({
        queryKey: ['adminMenuDetail', editingMenuId],
        queryFn: async () => {
            if (!editingMenuId) return null;
            const response = await apiClient.get(`/admin/menus/${editingMenuId}`);
            return response.data.data;
        },
        enabled: false // Only fetch manually when editing
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/menus/${id}`);
        },
        onSuccess: () => {
            message.success('Menu deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminMenus'] });
        },
        onError: () => {
            message.error('Failed to delete menu.');
        }
    });

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const title = values[`translations_${lang}_name`];
                if (!title) return null;
                return { languageCode: lang, name: title };
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error('At least one translation is required.');
            }

            const payload = {
                id: editingMenuId || undefined,
                parentId: values.parentId || null,
                url: values.url,
                orderIndex: values.orderIndex,
                isActive: values.isActive,
                openInNewWindow: values.openInNewWindow,
                translations
            };

            if (editingMenuId) {
                await apiClient.put(`/admin/menus/${editingMenuId}`, payload);
            } else {
                await apiClient.post('/admin/menus', payload);
            }
        },
        onSuccess: () => {
            message.success('Menu saved successfully.');
            setIsModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminMenus'] });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save menu.');
        }
    });

    const handleEdit = async (id: string) => {
        setEditingMenuId(id);
        const { data: menuDetail } = await fetchMenuDetails();
        if (menuDetail) {
            const formValues: any = {
                url: menuDetail.url,
                orderIndex: menuDetail.orderIndex,
                isActive: menuDetail.isActive,
                openInNewWindow: menuDetail.openInNewWindow,
                parentId: menuDetail.parentId
            };
            appLanguages.forEach(lang => {
                const trans = menuDetail.translations?.find((t: any) => t.languageCode === lang);
                if (trans) {
                    formValues[`translations_${lang}_name`] = trans.name;
                }
            });
            form.setFieldsValue(formValues);
            setIsModalVisible(true);
        }
    };

    const handleCreate = (parentId?: string) => {
        setEditingMenuId(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            orderIndex: 0,
            openInNewWindow: false,
            parentId: parentId || null
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this menu?',
            content: 'Any submenus might also be affected. This action cannot be undone.',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'URL', dataIndex: 'url', key: 'url' },
        { title: 'Order', dataIndex: 'orderIndex', key: 'orderIndex' },
        {
            title: 'Languages',
            dataIndex: 'availableLanguages',
            key: 'availableLanguages',
            render: (langs: string[]) => (
                <Space size={[0, 8]} wrap>
                    {langs?.map(l => <Tag key={l}>{l.toUpperCase()}</Tag>)}
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminMenuDto) => (
                <Space size="middle">
                    <Button size="small" icon={<PlusOutlined />} onClick={() => handleCreate(record.id)}>Add Submenu</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        children: (
            <Form.Item name={`translations_${lang}_name`} label="Menu Name">
                <Input placeholder={`Name in ${lang.toUpperCase()}`} />
            </Form.Item>
        )
    }));

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Menus Management</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate()}>
                    Create Root Menu
                </Button>
            </div>

            <Table
                dataSource={menus}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={false} // Tree tables usually don't paginate roots by default unless needed
            />

            <Modal
                title={editingMenuId ? 'Edit Menu' : 'Create Menu'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={saveMutation.isPending}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
                    <Form.Item name="parentId" hidden><Input /></Form.Item>

                    <Tabs type="card" items={tabItems} style={{ marginBottom: 24 }} />

                    <Form.Item name="url" label="URL (e.g., /about, or https://...)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Space size="large" style={{ display: 'flex' }}>
                        <Form.Item name="orderIndex" label="Order Index">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="openInNewWindow" label="Open in New Window" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};
